import type {
  AuthUser,
  CreatePersonalAccessTokenPayload,
  CreatePersonalAccessTokenResult,
  PersonalAccessToken,
  RegisterOrganizationPayload,
  RegisterOrganizationResponse,
  TenantJwtResponse,
  UserTenant,
} from "../../types";
import { handleUnauthorizedResponse } from "../authSession";
import {
  getCmsIdentityToken,
  getToken,
} from "../authToken";

export const MY_TENANTS_MAX_ATTEMPTS = 3;

let myTenantsAttemptCount = 0;

export function resetMyTenantsAttemptCount(): void {
  myTenantsAttemptCount = 0;
}

export function getMyTenantsAttemptCount(): number {
  return myTenantsAttemptCount;
}

export class AuthRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AuthRequestError";
    this.status = status;
  }
}

function getCmsApiBase(): string {
  const configured = import.meta.env.VITE_CMS_API_BASE as string | undefined;
  if (configured) return configured.replace(/\/$/, "");

  const graphqlEndpoint =
    (import.meta.env.VITE_GRAPHQL_ENDPOINT as string | undefined) ??
    "/api/v2/sami/cms/graphql";
  return graphqlEndpoint.replace(/\/graphql\/?$/, "");
}

async function authFetch<T>(
  path: string,
  bearerToken: string,
  init?: RequestInit & { skipAuthRetry?: boolean },
): Promise<T> {
  const { skipAuthRetry, ...fetchInit } = init ?? {};
  const url = `${getCmsApiBase()}${path}`;
  const res = await fetch(url, {
    ...fetchInit,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
      ...(fetchInit.headers ?? {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401 && !skipAuthRetry) {
      const refreshed = await handleUnauthorizedResponse();
      if (refreshed) {
        const retryBearer =
          path === "/auth/me"
            ? (getToken() ?? getCmsIdentityToken() ?? bearerToken)
            : (getCmsIdentityToken() ?? getToken() ?? bearerToken);
        return authFetch<T>(path, retryBearer, {
          ...fetchInit,
          skipAuthRetry: true,
        });
      }
    }

    const body = await res.text();
    throw new AuthRequestError(
      body.trim() || `Auth request failed: ${res.status} ${res.statusText}`,
      res.status,
    );
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export async function fetchMyTenants(
  bearerToken: string,
): Promise<UserTenant[]> {
  if (myTenantsAttemptCount >= MY_TENANTS_MAX_ATTEMPTS) {
    throw new Error(
      `Unable to load organizations after ${MY_TENANTS_MAX_ATTEMPTS} attempts`,
    );
  }

  myTenantsAttemptCount += 1;
  try {
    const memberships = await authFetch<UserTenant[]>(
      "/auth/my-tenants",
      bearerToken,
    );
    myTenantsAttemptCount = 0;
    return memberships;
  } catch (err) {
    throw err;
  }
}

export async function issueTenantJwt(
  tenantId: string,
  bearerToken: string,
): Promise<TenantJwtResponse> {
  return authFetch<TenantJwtResponse>("/auth/tenant-jwt", bearerToken, {
    method: "POST",
    body: JSON.stringify({ tenantId }),
  });
}

export async function fetchCmsMe(bearerToken: string): Promise<AuthUser> {
  const data = await authFetch<{
    email: string;
    tenantId?: string | null;
    roles?: string[];
    groups?: string[];
    permissions?: string[];
  }>("/auth/me", bearerToken);

  return {
    email: data.email,
    name: data.email,
    tenantId: data.tenantId ?? "",
    roles: data.roles ?? [],
    groups: data.groups ?? [],
    permissions: data.permissions ?? [],
  };
}

export async function registerOrganization(
  bearerToken: string,
  payload: RegisterOrganizationPayload,
): Promise<RegisterOrganizationResponse> {
  return authFetch<RegisterOrganizationResponse>(
    "/auth/register-organization",
    bearerToken,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

function requireTenantJwt(): string {
  const token = getToken();
  if (!token) {
    throw new AuthRequestError("Tenant session is required", 401);
  }
  return token;
}

export async function listPersonalAccessTokens(): Promise<PersonalAccessToken[]> {
  return authFetch<PersonalAccessToken[]>(
    "/auth/personal-access-tokens",
    requireTenantJwt(),
  );
}

export async function createPersonalAccessToken(
  payload: CreatePersonalAccessTokenPayload,
): Promise<CreatePersonalAccessTokenResult> {
  return authFetch<CreatePersonalAccessTokenResult>(
    "/auth/personal-access-tokens",
    requireTenantJwt(),
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function revokePersonalAccessToken(tokenId: string): Promise<void> {
  await authFetch<void>(
    `/auth/personal-access-tokens/${tokenId}`,
    requireTenantJwt(),
    { method: "DELETE" },
  );
}

export { getCmsApiBase };
